<template>
    <div class="flex mt-[42px] mb-[42px] justify-between h-[42px]">
        <h1
            class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
        >
            <span class="flex flex-wrap items-center gap-2">
                <span class="flex items-center gap-2">
                    <span
                        class="h-2 w-2 rounded-full"
                        :class="
                            reportedDomain.active
                                ? 'bg-green-500'
                                : 'bg-gray-400'
                        "
                    ></span>
                    <a
                        :href="
                            route('reports.show', {
                                domain: reportedDomain.domain,
                            })
                        "
                        class="text-gray-800 hover:text-gray-900"
                    >
                        {{ reportedDomain.domain }}
                    </a>
                </span>
                <template v-if="source">
                    <svg
                        class="h-[6px] w-[6px] text-gray-800"
                        viewBox="0 0 6 6"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="0.9"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M2 1l2 2-2 2" />
                    </svg>
                    <a
                        :href="
                            route('reports.show.source', {
                                domain: reportedDomain.domain,
                                source: source,
                            })
                        "
                        class="text-gray-800 hover:text-gray-900"
                    >
                        {{ source }}
                    </a>
                </template>
                <template v-if="ip">
                    <svg
                        class="h-[6px] w-[6px] text-gray-800"
                        viewBox="0 0 6 6"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="0.9"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M2 1l2 2-2 2" />
                    </svg>
                    <span class="text-gray-800">{{ ip }}</span>
                </template>
            </span>
        </h1>

        <div>
            <DateRangeField
                :date-range="dateRange"
                :reported-domain="reportedDomain"
                :display-full-block="true"
                route-name="reports.show.ip"
                :route-params="{
                    domain: reportedDomain.domain,
                    source: source,
                    ip: ip,
                }"
            />
        </div>
    </div>

    <div class="mb-[42px]">
        <FreePlanReportsLimitBlock />
    </div>

    <div>
        <StatsBar
            :stats="{ pass: reports.total.pass, fail: reports.total.fail }"
            :stats-before="null"
            :statistics="statistics"
        />
    </div>

    <div class="mt-[22px] grid grid-cols-1 gap-[22px] lg:grid-cols-2">
        <div class="rounded-[12.8px] border border-gray-200 bg-white p-6">
            <h3 class="mb-4 text-sm font-semibold text-gray-900">
                {{ $t("vue.authentication_summary") }}
            </h3>
            <div class="space-y-4">
                <div
                    class="flex items-start gap-4 rounded-lg border border-green-100 bg-green-50 p-4"
                    :class="{
                        'border-yellow-100 bg-yellow-50':
                            passRates.dmarc < 75 && passRates.dmarc > 50,
                        'border-red-100 bg-red-50': passRates.dmarc <= 50,
                    }"
                >
                    <div
                        class="mt-2 h-2 w-2 shrink-0 rounded-full bg-green-500"
                        :class="{
                            'bg-yellow-500':
                                passRates.dmarc < 75 && passRates.dmarc > 50,
                            'bg-red-500': passRates.dmarc <= 50,
                        }"
                    ></div>
                    <div>
                        <p class="mb-1 font-medium text-green-900">
                            {{ generateDmarcSummary(passRates).headline }}
                        </p>
                        <p class="text-sm text-green-800">
                            {{ generateDmarcSummary(passRates).summary }}
                        </p>
                    </div>
                </div>
                <div
                    class="flex items-start gap-4 rounded-lg border border-blue-100 bg-blue-50 p-4"
                >
                    <div
                        class="mt-2 h-2 w-2 shrink-0 rounded-full bg-blue-500"
                    ></div>
                    <div>
                        <p class="mb-1 font-medium text-blue-900">
                            {{ $t("vue.volume_analysis") }}
                        </p>
                        <p class="text-sm text-blue-800">
                            {{
                                t("vue.peak_volume_processed", {
                                    count: new Intl.NumberFormat().format(
                                        peakDay.pass + peakDay.fail,
                                    ),
                                })
                            }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <div class="rounded-[12.8px] border border-gray-200 bg-white p-6">
            <h3 class="mb-4 text-sm font-semibold text-gray-900">
                {{ $t("vue.alignment_issues") }}
            </h3>
            <div class="space-y-4">
                <div
                    v-if="firstInvalidSpf && firstInvalidSpf[1]"
                    class="flex items-start gap-4 rounded-lg border border-yellow-100 bg-yellow-50 p-4"
                >
                    <div
                        class="mt-2 h-2 w-2 shrink-0 rounded-full bg-yellow-500"
                    ></div>
                    <div>
                        <p class="mb-1 font-medium text-yellow-900">
                            {{ $t("vue.spf_not_aligned") }}
                        </p>
                        <p class="text-sm text-yellow-800">
                            {{
                                t("vue.spf_not_aligned_detail", {
                                    count: new Intl.NumberFormat().format(
                                        firstInvalidSpf[1].spf_invalid,
                                    ),
                                    domain: firstInvalidSpf[1].auth_results.spf
                                        .domain,
                                })
                            }}
                        </p>
                    </div>
                </div>
                <div class="flex items-center justify-between py-2">
                    <span class="text-gray-600">{{
                        $t("vue.spf_pass_rate")
                    }}</span
                    ><span
                        class="font-medium text-green-600"
                        :style="{
                            color: calculateColorByPercents(passRates.spf),
                        }"
                        >{{ passRates.spf }}%</span
                    >
                </div>
                <div class="flex items-center justify-between py-2">
                    <span class="text-gray-600">{{
                        $t("vue.dkim_pass_rate")
                    }}</span
                    ><span
                        class="font-medium text-green-600"
                        :style="{
                            color: calculateColorByPercents(passRates.dkim),
                        }"
                        >{{ passRates.dkim }}%</span
                    >
                </div>
                <div class="flex items-center justify-between py-2">
                    <span class="text-gray-600">{{
                        $t("vue.dmarc_pass_rate")
                    }}</span
                    ><span
                        class="font-medium text-green-600"
                        :style="{
                            color: calculateColorByPercents(passRates.dmarc),
                        }"
                        >{{ passRates.dmarc }}%</span
                    >
                </div>
            </div>
        </div>
    </div>

    <div
        v-if="reports.total.pass > 0 || reports.total.fail > 0"
        class="mt-[22px]"
    >
        <StatsChart
            :chart-data="reports.chartData"
            :header="t('vue.daily_reports_from_this_ip_addr')"
        ></StatsChart>
    </div>

    <div class="mb-[100px]">
        <DynamicContentTable
            :title="$t('vue.email_authentication_results')"
            :subtitle="$t('vue.email_authentication_results_description')"
            :rows="tableRows"
            :columns="tableColumns"
            :row-click="showDetails"
            :endpoint="
                () =>
                    route('reports.show.ip', {
                        domain: reportedDomain.domain,
                        source: source,
                        ip: ip,
                    })
            "
            :base-query="tableBaseQuery"
            :table="table"
            :pagination="pagination"
            row-key="event_key"
            :search-placeholder="t('vue.search')"
            :empty-text="
                page.props.hasFreePlan
                    ? t('vue.no_or_nullified_results')
                    : t('vue.no_results')
            "
            :per-page-label="t('vue.per_page')"
            :class="
                !(reports.total.pass > 0 || reports.total.fail > 0)
                    ? 'mt-[22px]'
                    : ''
            "
        >
            <template #header-extra>
                <div class="flex flex-wrap items-center justify-between gap-3">
                    <div class="flex flex-wrap items-center gap-3">
                        <div class="flex items-center gap-2">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-funnel h-4 w-4 text-gray-500"
                                aria-hidden="true"
                            >
                                <path
                                    d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z"
                                ></path>
                            </svg>
                            <span class="text-sm font-medium text-gray-700"
                                >{{ $t("vue.filters") }}:</span
                            >
                        </div>
                        <button
                            data-slot="button"
                            class="[&amp;_svg]:pointer-events-none [&amp;_svg:not([class*='size-'])]:size-4 [&amp;_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-background hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-red-200 px-3 text-sm font-medium text-red-700 outline-hidden transition-all hover:bg-red-50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 has-[>svg]:px-2.5"
                            :class="{
                                'bg-red-600 text-white hover:bg-red-700':
                                    filters.includes('dmarc'),
                            }"
                            @click="addFilter('dmarc')"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-circle-x h-3 w-3"
                                aria-hidden="true"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <path d="m15 9-6 6"></path>
                                <path d="m9 9 6 6"></path>
                            </svg>
                            {{ $t("vue.dmarc_failed") }}
                        </button>
                        <button
                            data-slot="button"
                            class="[&amp;_svg]:pointer-events-none [&amp;_svg:not([class*='size-'])]:size-4 [&amp;_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-background hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-yellow-200 px-3 text-sm font-medium text-yellow-700 outline-hidden transition-all hover:bg-yellow-50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 has-[>svg]:px-2.5"
                            :class="{
                                'bg-yellow-600 text-white! hover:bg-yellow-700':
                                    filters.includes('spf'),
                            }"
                            @click="addFilter('spf')"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-circle-alert h-3 w-3"
                                aria-hidden="true"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" x2="12" y1="8" y2="12"></line>
                                <line x1="12" x2="12.01" y1="16" y2="16"></line>
                            </svg>
                            {{ $t("vue.spf_issues") }}
                        </button>
                        <button
                            data-slot="button"
                            class="[&amp;_svg]:pointer-events-none [&amp;_svg:not([class*='size-'])]:size-4 [&amp;_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-background hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 hover:bg-orange-50 inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-orange-200 px-3 text-sm font-medium text-orange-700 outline-hidden transition-all focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 has-[>svg]:px-2.5"
                            :class="{
                                'bg-orange-600 text-white! hover:bg-orange-700':
                                    filters.includes('dkim'),
                            }"
                            @click="addFilter('dkim')"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-circle-alert h-3 w-3"
                                aria-hidden="true"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" x2="12" y1="8" y2="12"></line>
                                <line x1="12" x2="12.01" y1="16" y2="16"></line>
                            </svg>
                            {{ $t("vue.dkim_issues") }}
                        </button>
                        <button
                            v-if="filters.length"
                            data-slot="button"
                            class="[&amp;_svg]:pointer-events-none [&amp;_svg:not([class*='size-'])]:size-4 [&amp;_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:hover:bg-accent/50 inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-medium text-gray-600 outline-hidden transition-all hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 has-[>svg]:px-2.5"
                            @click="clearFilters()"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-x h-3 w-3"
                                aria-hidden="true"
                            >
                                <path d="M18 6 6 18"></path>
                                <path d="m6 6 12 12"></path>
                            </svg>
                            {{ $t("vue.clear_all") }}
                        </button>
                    </div>
                    <div class="text-sm text-gray-600">
                        <span v-if="hasTableFilters">
                            {{
                                t("vue.showing_results_of", {
                                    shown: filteredTotal,
                                    total: totalEvents,
                                })
                            }}
                        </span>
                        <span v-else>
                            {{
                                t("vue.total_results", {
                                    total: totalEvents,
                                })
                            }}
                        </span>
                    </div>
                </div>
            </template>

            <template #row="{ row }">
                <td class="px-6 py-4">
                    <span class="font-medium text-gray-900">{{
                        new Intl.NumberFormat().format(row.sum)
                    }}</span>
                </td>

                <td class="px-6 py-4">
                    <button
                        type="button"
                        class="group/domain -m-2 flex cursor-pointer items-center gap-3 rounded-lg p-2 text-left transition-colors duration-150 hover:bg-blue-50"
                    >
                        <span
                            class="font-medium text-blue-600 transition-colors duration-150 hover:text-blue-800 group-hover/domain:underline"
                            >{{ row.identifiers.header_from }}</span
                        >
                    </button>
                </td>
                <td class="px-6 py-4">
                    <div class="space-y-2">
                        <div
                            v-if="row.auth_results.spf.result === 'pass'"
                            class="mr-2 inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-circle-check-big h-3 w-3"
                                aria-hidden="true"
                            >
                                <path
                                    d="M21.801 10A10 10 0 1 1 17 3.335"
                                ></path>
                                <path d="m9 11 3 3L22 4"></path>
                            </svg>
                            <span class="capitalize">{{ $t("vue.pass") }}</span>
                        </div>
                        <div
                            v-else
                            class="mr-2 inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-circle-alert h-3 w-3"
                                aria-hidden="true"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" x2="12" y1="8" y2="12"></line>
                                <line x1="12" x2="12.01" y1="16" y2="16"></line>
                            </svg>
                            <span class="capitalize">{{ $t("vue.fail") }}</span>
                        </div>

                        <div
                            v-if="
                                row.auth_results.spf.domain ===
                                row.identifiers.header_from
                            "
                            class="mr-2 inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-circle-alert h-3 w-3"
                                aria-hidden="true"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" x2="12" y1="8" y2="12"></line>
                                <line x1="12" x2="12.01" y1="16" y2="16"></line>
                            </svg>
                            <span class="capitalize">{{
                                $t("vue.aligned")
                            }}</span>
                        </div>
                        <div
                            v-else
                            class="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-circle-alert h-3 w-3"
                                aria-hidden="true"
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" x2="12" y1="8" y2="12"></line>
                                <line x1="12" x2="12.01" y1="16" y2="16"></line>
                            </svg>
                            <span class="capitalize">{{
                                $t("vue.not_aligned")
                            }}</span>
                        </div>

                        <div
                            v-if="Array.isArray(row.auth_results.spf) === true"
                        >
                            <div
                                v-for="spfResult in row.auth_results.spf"
                                :key="spfResult"
                                class="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-500 mt-1"
                            >
                                {{ spfResult.domain }}
                            </div>
                        </div>

                        <div
                            v-else-if="
                                row.auth_results.spf &&
                                typeof row.auth_results.spf === 'object' &&
                                row.auth_results.spf.domain !== ''
                            "
                            class="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-500 mt-1"
                        >
                            {{ row.auth_results.spf.domain }}
                        </div>

                        <div
                            v-else
                            class="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-500 mt-1"
                        >
                            {{ $t("vue.spf_domain_not_available") }}
                        </div>
                    </div>
                </td>

                <td class="px-6 py-4">
                    <template
                        v-if="
                            row.auth_results.dkim !== undefined &&
                            row.auth_results.dkim.constructor === Array
                        "
                    >
                        <div
                            v-for="record in row.auth_results.dkim"
                            :key="record.domain"
                            class="mb-1 space-y-2 mb-5"
                        >
                            <div
                                v-if="record.result === 'pass'"
                                class="mr-2 inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-circle-check-big h-3 w-3"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M21.801 10A10 10 0 1 1 17 3.335"
                                    ></path>
                                    <path d="m9 11 3 3L22 4"></path>
                                </svg>
                                <span class="capitalize">pass</span>
                            </div>
                            <div
                                v-else-if="record.result === 'neutral'"
                                class="mr-2 inline-flex items-center gap-1 rounded-full border border-yellow-200 bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-circle-check-big h-3 w-3"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M21.801 10A10 10 0 1 1 17 3.335"
                                    ></path>
                                    <path d="m9 11 3 3L22 4"></path>
                                </svg>
                                <span class="capitalize">neutral</span>
                            </div>
                            <div
                                v-else
                                class="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 mr-2"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-circle-alert h-3 w-3"
                                    aria-hidden="true"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" x2="12" y1="8" y2="12"></line>
                                    <line
                                        x1="12"
                                        x2="12.01"
                                        y1="16"
                                        y2="16"
                                    ></line>
                                </svg>
                                <span class="capitalize">{{
                                    $t("vue.fail")
                                }}</span>
                            </div>

                            <div
                                v-if="
                                    record.domain ===
                                    row.identifiers.header_from
                                "
                                class="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-circle-check-big h-3 w-3"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M21.801 10A10 10 0 1 1 17 3.335"
                                    ></path>
                                    <path d="m9 11 3 3L22 4"></path>
                                </svg>
                                <span class="capitalize">{{
                                    $t("vue.aligned")
                                }}</span>
                            </div>
                            <div
                                v-else
                                class="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-circle-alert h-3 w-3"
                                    aria-hidden="true"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" x2="12" y1="8" y2="12"></line>
                                    <line
                                        x1="12"
                                        x2="12.01"
                                        y1="16"
                                        y2="16"
                                    ></line>
                                </svg>
                                <span class="capitalize">{{
                                    $t("vue.not_aligned")
                                }}</span>
                            </div>
                            <div
                                class="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-500"
                            >
                                {{ record.domain }}
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="mb-1 space-y-2 mb-5">
                            <div
                                v-if="
                                    row.auth_results.dkim &&
                                    row.auth_results.dkim.result === 'pass'
                                "
                                class="mr-2 inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-circle-check-big h-3 w-3"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M21.801 10A10 10 0 1 1 17 3.335"
                                    ></path>
                                    <path d="m9 11 3 3L22 4"></path>
                                </svg>
                                <span class="capitalize">{{
                                    $t("vue.pass")
                                }}</span>
                            </div>
                            <div
                                v-else
                                class="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 mr-2"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-circle-alert h-3 w-3"
                                    aria-hidden="true"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" x2="12" y1="8" y2="12"></line>
                                    <line
                                        x1="12"
                                        x2="12.01"
                                        y1="16"
                                        y2="16"
                                    ></line>
                                </svg>
                                <span class="capitalize">{{
                                    $t("vue.fail")
                                }}</span>
                            </div>

                            <div
                                v-if="
                                    row.auth_results.dkim &&
                                    row.auth_results.dkim.domain ===
                                        row.identifiers.header_from
                                "
                                class="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-circle-check-big h-3 w-3"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M21.801 10A10 10 0 1 1 17 3.335"
                                    ></path>
                                    <path d="m9 11 3 3L22 4"></path>
                                </svg>
                                <span class="capitalize">{{
                                    $t("vue.aligned")
                                }}</span>
                            </div>
                            <div
                                v-else
                                class="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-circle-alert h-3 w-3"
                                    aria-hidden="true"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" x2="12" y1="8" y2="12"></line>
                                    <line
                                        x1="12"
                                        x2="12.01"
                                        y1="16"
                                        y2="16"
                                    ></line>
                                </svg>
                                <span class="capitalize">{{
                                    $t("vue.not_aligned")
                                }}</span>
                            </div>

                            <div
                                v-if="
                                    row.auth_results.dkim !== undefined &&
                                    row.auth_results.dkim.domain
                                "
                                class="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-500 mt-1"
                            >
                                {{ row.auth_results.dkim.domain }}
                            </div>
                            <div
                                v-else
                                class="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-500 mt-1"
                            >
                                {{ $t("vue.dkim_domain_not_available") }}
                            </div>
                        </div>
                    </template>
                </td>

                <td class="px-6 py-4 text-center">
                    <svg
                        v-if="row.fail > 0"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-circle-x mx-auto h-4 w-4 text-red-500"
                        aria-hidden="true"
                    >
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="m15 9-6 6"></path>
                        <path d="m9 9 6 6"></path>
                    </svg>
                    <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-circle-check-big mx-auto h-4 w-4 text-green-500"
                        aria-hidden="true"
                    >
                        <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                        <path d="m9 11 3 3L22 4"></path>
                    </svg>
                </td>
            </template>
        </DynamicContentTable>
    </div>

    <DetailedModal
        v-if="showDetail"
        :detail-row="detailRow"
        :ip="ip"
        :reported-domain="reportedDomain"
        @close="hideDetailModal"
    />

    <FreePlanMaxReachedPopup />
</template>

<script lang="ts" setup>
import { computed, ref, watch, type PropType } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import DynamicContentTable from "@/components/Content/DynamicContentTable.vue";
import DateRangeField from "@/Pages/Reports/components/DateRangeField.vue";
import DetailedModal from "@/Pages/Reports/components/DetailedModal.vue";
import StatsBar from "@/Pages/Reports/components/StatsBar.vue";
import StatsChart from "@/Pages/Reports/components/StatsChart.vue";
import { useTranslations } from "@/composables/useTranslations.ts";
import FreePlanMaxReachedPopup from "@/components/Atomic/Dialogs/Popups/FreePlanMaxReachedPopup.vue";
import FreePlanReportsLimitBlock from "@/Pages/Reports/components/FreePlanReportsLimitBlock.vue";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps.ts";

const page = usePage<IRhizomPageProps>();
const { t } = useTranslations();

interface ReportsEvent {
    pass: number;
    spf_valid: number;
    spf_invalid: number;
    dkim_valid: number;
    dkim_invalid: number;
    fail: number;
    sum: number;
    event_key?: string;
    identifiers: {
        header_from: string;
        envelope_from?: string;
        envelope_to?: string;
    };
    auth_results: Record<string, any>;
}

interface ReportsData {
    total: {
        pass: number;
        fail: number;
    };
    events: Record<string, ReportsEvent>;
    chartData: Array<{
        day: string;
        pass: number;
        fail: number;
    }>;
}

interface ReportedDomain {
    domain: string;
    active: boolean;
    dmarcPolicy?: string;
}

type TableFilterKey = "dmarc" | "spf" | "dkim";

type TableState = {
    q?: string;
    sort_by?: string | null;
    sort_dir?: "asc" | "desc" | null;
    per_page?: number;
    current_page?: number;
};

type Pagination = {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number | null;
    to: number | null;
};

const props = defineProps({
    reports: {
        type: Object as PropType<ReportsData>,
        required: true,
    },
    source: {
        type: String,
        required: true,
    },
    ip: {
        type: String,
        required: true,
    },
    reportedDomain: {
        type: Object as PropType<ReportedDomain>,
        required: true,
    },
    dateRange: {
        type: Object as PropType<{
            start: string;
            end: string;
        }>,
        required: true,
    },
    tableRows: {
        type: Array as PropType<ReportsEvent[]>,
        required: true,
    },
    table: {
        type: Object as PropType<TableState>,
        required: true,
    },
    pagination: {
        type: Object as PropType<Pagination>,
        required: true,
    },
    tableFilters: {
        type: Array as PropType<TableFilterKey[]>,
        default: () => [],
    },
});

const filters = ref<TableFilterKey[]>([...(props.tableFilters ?? [])]);

watch(
    () => props.tableFilters,
    (next) => {
        filters.value = [...(next ?? [])];
    },
);

const tableColumns = computed(() => [
    {
        key: "emails",
        label: t("vue.emails"),
        sortable: true,
        sortKey: "emails",
        colClass: "w-[14%]",
    },
    {
        key: "from_domain",
        label: t("vue.from_domain"),
        sortable: true,
        sortKey: "from_domain",
        colClass: "w-[22%]",
    },
    {
        key: "spf",
        label: t("vue.spf_result_alignment"),
        sortable: true,
        sortKey: "spf",
        colClass: "w-[26%]",
    },
    {
        key: "dkim",
        label: t("vue.dkim_result_alignment"),
        sortable: true,
        sortKey: "dkim",
        colClass: "w-[26%]",
    },
    {
        key: "dmarc",
        label: t("vue.dmarc_status"),
        sortable: true,
        sortKey: "dmarc",
        colClass: "w-[12%]",
        thClass: "text-center",
    },
]);

const tableBaseQuery = computed(() => ({
    start_date: props.dateRange.start,
    end_date: props.dateRange.end,
    filters: filters.value.length ? filters.value : undefined,
}));

const totalEvents = computed(() => Object.keys(props.reports.events).length);
const filteredTotal = computed(
    () => props.pagination?.total ?? props.tableRows.length,
);
const hasTableFilters = computed(
    () => filters.value.length > 0 || (props.table?.q ?? "") !== "",
);

const passRates = computed(() => {
    let spfValid = 0;
    let dkimValid = 0;
    let dmarcValid = 0;
    let total = 0;

    for (const row of Object.values(props.reports.events)) {
        spfValid += row.spf_valid ? row.sum : 0;
        dkimValid += row.dkim_valid ? row.sum : 0;
        dmarcValid += row.fail === 0 ? row.sum : 0;
        total += row.sum;
    }

    return {
        spf: total ? Math.floor((spfValid / total) * 1000) / 10 : 0,
        dkim: total ? Math.floor((dkimValid / total) * 1000) / 10 : 0,
        dmarc: total ? Math.floor((dmarcValid / total) * 1000) / 10 : 0,
    };
});

const statistics = computed(() => {
    const total = props.reports.total.pass + props.reports.total.fail;

    return {
        totalEmailsReported: total,
        dmarcCompliant: props.reports.total.pass,
        dmarcNonCompliant: props.reports.total.fail,
        complianceRate: total
            ? Math.round((props.reports.total.pass / total) * 100)
            : 0,
    };
});

const peakDay = computed(() => {
    return props.reports.chartData.reduce((peak, current) => {
        const currentTotal = current.pass + current.fail;
        const peakTotal = peak.pass + peak.fail;
        return currentTotal > peakTotal ? current : peak;
    });
});

function hideDetailModal() {
    showDetail.value = false;
}

const detailRow = ref<ReportsEvent | null>(null);
const showDetail = ref(false);

const showDetails = (row: ReportsEvent) => {
    detailRow.value = row;
    showDetail.value = true;
};

const firstInvalidSpf = computed(() => {
    return Object.entries(props.reports.events).find(
        ([_, event]) => event.spf_invalid > 0,
    );
});

const buildTableQuery = (overrides: Record<string, any> = {}) => {
    const query: Record<string, any> = {
        ...tableBaseQuery.value,
        q: props.table?.q || undefined,
        per_page: props.table?.per_page ?? undefined,
        current_page: props.table?.current_page ?? 1,
        ...overrides,
    };

    if (props.table?.sort_by && props.table?.sort_dir) {
        query.sort_by = props.table.sort_by;
        query.sort_dir = props.table.sort_dir;
    }

    return query;
};

const applyTableFilters = (nextFilters: TableFilterKey[]) => {
    filters.value = [...nextFilters];
    router.get(
        route("reports.show.ip", {
            domain: props.reportedDomain.domain,
            source: props.source,
            ip: props.ip,
        }),
        buildTableQuery({
            current_page: 1,
            filters: nextFilters.length ? nextFilters : undefined,
        }),
        {
            preserveScroll: true,
            preserveState: true,
            replace: true,
        },
    );
};

const addFilter = (filter: TableFilterKey) => {
    const nextFilters = filters.value.includes(filter)
        ? filters.value.filter((e) => e !== filter)
        : [...filters.value, filter];
    applyTableFilters(nextFilters);
};

const generateDmarcSummary = (passRates) => {
    let headline;

    if (
        passRates.spf === 100 &&
        passRates.dkim === 100 &&
        passRates.dmarc >= 99
    ) {
        headline = t("vue.dmarc_summary_excellent");
    } else if (passRates.dmarc >= 95) {
        headline = t("vue.dmarc_summary_good");
    } else if (passRates.dmarc >= 80) {
        headline = t("vue.dmarc_summary_moderate");
    } else {
        headline = t("vue.dmarc_summary_needs_attention");
    }

    const formatted = (value) => value.toFixed(1).replace(/\.0$/, "") + "%";

    return {
        headline: headline,
        summary: t("vue.dmarc_summary_text", {
            spf: formatted(passRates.spf),
            dkim: formatted(passRates.dkim),
            dmarc: formatted(passRates.dmarc),
        }),
    };
};

const clearFilters = () => {
    applyTableFilters([]);
};

const calculateColorByPercents = (percents) => {
    if (percents > 75) {
        return "#15803D";
    }

    if (percents > 50) {
        return "#CA8A04";
    }

    return "#B91C1C";
};
</script>
